import { Check, Lock, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { RoleDef, RolesResponse } from '../api/types';
import { useAuth } from '../auth';
import { Topbar } from '../components/Layout';
import { confirmAction, Field, Modal, PageLoader, useToast } from '../components/ui';
import { useI18n, type MessageKey } from '../i18n';
import { roleDescription, roleLabel } from '../roles';

type Draft = Pick<RoleDef, 'key' | 'name' | 'description' | 'permissions' | 'allSurveys'>;

const blank: Draft = { key: '', name: '', description: '', permissions: [], allSurveys: false };

/** Only what the API accepts: it rejects unknown fields such as builtIn, userCount and updatedAt. */
export function toDraft(r: Draft): Draft {
  return { key: r.key, name: r.name, description: r.description, permissions: [...r.permissions], allSurveys: r.allSurveys };
}

/** Role settings (RBAC): which permissions each role has and which questionnaires it reaches. */
export function RolesPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const toast = useToast();
  const [data, setData] = useState<RolesResponse | null>(null);
  // null: closed; key '': a new role.
  const [editing, setEditing] = useState<{ draft: Draft; isNew: boolean } | null>(null);
  const isAdmin = user?.role === 'admin';

  const load = useCallback(() => {
    api.roles().then(setData, (e) => toast(e instanceof ApiError ? e.message : t('roles.load_failed'), 'error'));
  }, [toast, t]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  // Editing roles grants permissions, so it is for the built-in admin role only (the API agrees).
  if (!isAdmin) return <Navigate to="/" replace />;
  if (!data) return <PageLoader />;

  const locked = new Set(data.locked);
  const perm = (a: string) => t(`perm.${a}` as MessageKey);
  // Respondents and enumerators reach questionnaires only through their own flows.
  const ownFlow = (r: RoleDef) => locked.has(r.key) && r.key !== 'admin';

  async function remove(r: RoleDef) {
    if (!confirmAction(t('roles.delete_confirm', { name: roleLabel(t, r.key, r.name) }))) return;
    try {
      await api.deleteRole(r.key);
      toast(t('roles.deleted'));
      load();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
    }
  }

  return (
    <>
      <Topbar title={t('roles.title')}>
        <button className="btn primary" onClick={() => setEditing({ draft: blank, isNew: true })}>
          <Plus /> {t('roles.new')}
        </button>
      </Topbar>

      <p className="muted mb-16">
        {t('roles.sub')} <Link to="/admin/users">{t('roles.assign_link')}</Link>
      </p>

      <div className="grid cols-2 mb-16">
        {data.roles.map((r) => (
          <div key={r.key} className="card card-pad stack gap-8">
            <div className="row gap-8">
              <h3 className="grow row gap-8">
                {r.key === 'admin' ? <ShieldCheck className="icon-sm" /> : null}
                {roleLabel(t, r.key, r.name)}
              </h3>
              {locked.has(r.key) ? (
                <span className="pill" title={t('roles.locked_hint')}>
                  <Lock size={12} /> {t('roles.locked')}
                </span>
              ) : r.builtIn ? (
                <span className="pill">{t('roles.built_in')}</span>
              ) : (
                <span className="pill green">{t('roles.custom')}</span>
              )}
            </div>
            <div className="small muted">{roleDescription(t, r)}</div>
            <div className="small">
              {ownFlow(r) ? t('roles.scope_own_flow') : r.allSurveys ? t('roles.scope_all') : t('roles.scope_shared')} ·{' '}
              {t('roles.permission_count', { count: r.permissions.length })} ·{' '}
              {t('roles.user_count', { count: r.userCount })}
            </div>
            <div className="row gap-8 mt-8">
              <button className="btn sm" onClick={() => setEditing({ draft: toDraft(r), isNew: false })}>
                {t('roles.edit')}
              </button>
              {!r.builtIn && (
                <button
                  className="btn sm danger"
                  onClick={() => void remove(r)}
                  disabled={r.userCount > 0}
                  title={r.userCount > 0 ? t('roles.delete_in_use') : undefined}
                >
                  <Trash2 size={14} /> {t('roles.delete')}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-head">
          <div className="grow">
            <h2>{t('roles.matrix')}</h2>
            <div className="sub">{t('roles.matrix_sub')}</div>
          </div>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('roles.permission')}</th>
                {data.roles.map((r) => (
                  <th key={r.key} className="num">
                    {roleLabel(t, r.key, r.name)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('roles.scope')}</td>
                {data.roles.map((r) => (
                  <td key={r.key} className="num small">
                    {ownFlow(r) ? '—' : r.allSurveys ? t('roles.scope_all_short') : t('roles.scope_shared_short')}
                  </td>
                ))}
              </tr>
              {data.permissionGroups.flatMap((g) => [
                <tr key={g.key}>
                  <th colSpan={data.roles.length + 1} className="small muted">
                    {t(`perm.group.${g.key}` as MessageKey)}
                  </th>
                </tr>,
                ...g.actions.map((a) => (
                  <tr key={a}>
                    <td title={t(`perm.${a}.hint` as MessageKey)}>{perm(a)}</td>
                    {data.roles.map((r) => (
                      <td key={r.key} className="num">
                        {r.permissions.includes(a) ? <Check size={16} aria-label={t('roles.allowed')} /> : <span className="muted">—</span>}
                      </td>
                    ))}
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <RoleEditor
          initial={editing.draft}
          isNew={editing.isNew}
          locked={locked.has(editing.draft.key)}
          groups={data.permissionGroups}
          onClose={() => setEditing(null)}
          onSaved={() => {
            toast(editing.isNew ? t('roles.created') : t('roles.saved'));
            setEditing(null);
            load();
          }}
        />
      )}
    </>
  );
}

function RoleEditor({
  initial,
  isNew,
  locked,
  groups,
  onClose,
  onSaved,
}: {
  initial: Draft;
  isNew: boolean;
  locked: boolean;
  groups: RolesResponse['permissionGroups'];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState(initial);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function toggle(a: string, on: boolean) {
    setForm((f) => ({ ...f, permissions: on ? [...f.permissions, a] : f.permissions.filter((p) => p !== a) }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFields({});
    setError('');
    try {
      const { key, ...body } = toDraft(form);
      await (isNew ? api.createRole({ key, ...body }) : api.updateRole(key, body));
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        setFields(err.fields);
        if (!Object.keys(err.fields).length) setError(err.message);
      }
      setBusy(false);
    }
  }

  return (
    <Modal
      title={isNew ? t('roles.new') : t('roles.edit_title', { name: roleLabel(t, initial.key, initial.name) })}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn primary" form="role-editor" disabled={busy}>
            {busy ? t('roles.saving') : t('roles.save')}
          </button>
        </>
      }
    >
      <form id="role-editor" className="form-grid" onSubmit={submit}>
        {error && <div className="banner error">{error}</div>}
        {isNew && (
          <Field label={t('roles.key')} error={fields.key} hint={t('roles.key_hint')}>
            <input
              className="input"
              required
              value={form.key}
              pattern="[a-z][a-z0-9_]{1,31}"
              onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
            />
          </Field>
        )}
        <Field label={t('roles.name')} error={fields.name}>
          <input className="input" required maxLength={60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={t('roles.description')} error={fields.description}>
          <textarea className="input" rows={2} maxLength={300} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>

        {locked && <div className="banner">{t('roles.locked_hint')}</div>}

        <fieldset className="stack gap-8" disabled={locked} style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="label">{t('roles.scope')}</legend>
          <label className="check">
            <input type="radio" name="scope" checked={!form.allSurveys} onChange={() => setForm({ ...form, allSurveys: false })} />
            <span>
              {t('roles.scope_shared')}
              <span className="hint" style={{ display: 'block' }}>
                {t('roles.scope_shared_hint')}
              </span>
            </span>
          </label>
          <label className="check">
            <input type="radio" name="scope" checked={form.allSurveys} onChange={() => setForm({ ...form, allSurveys: true })} />
            <span>
              {t('roles.scope_all')}
              <span className="hint" style={{ display: 'block' }}>
                {t('roles.scope_all_hint')}
              </span>
            </span>
          </label>
        </fieldset>

        {fields.permissions && <div className="banner error">{fields.permissions}</div>}
        {groups.map((g) => (
          <fieldset key={g.key} className="stack gap-8" disabled={locked} style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="label">{t(`perm.group.${g.key}` as MessageKey)}</legend>
            {g.actions.map((a) => (
              <label key={a} className="check">
                <input type="checkbox" checked={form.permissions.includes(a)} onChange={(e) => toggle(a, e.target.checked)} />
                <span>
                  {t(`perm.${a}` as MessageKey)}
                  <span className="hint" style={{ display: 'block' }}>
                    {t(`perm.${a}.hint` as MessageKey)}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
        ))}
      </form>
    </Modal>
  );
}
